import { useCallback, useLayoutEffect, useState } from "react";

/**
 * Positions a dropdown panel with `position: fixed` anchored to its control so
 * it escapes any scrollable/overflow-clipping ancestor (e.g. the horizontally
 * scrolling filter-chip row). The panel stays in the DOM next to its control,
 * so scoped styles and click-outside handling keep working.
 */
const useFloatingPanel = (isOpen, controlRef, options = {}) => {
  const [style, setStyle] = useState(null);
  const zIndex = options?.zIndex ?? 1080;

  const update = useCallback(() => {
    const control = controlRef.current;
    if (!control) return;
    const rect = control.getBoundingClientRect();
    const gap = 6;
    const margin = 8;
    const viewportHeight = window.innerHeight;
    const viewportWidth = window.innerWidth;
    const spaceBelow = viewportHeight - rect.bottom;
    const preferredHeight = 320;
    const openUp = spaceBelow < preferredHeight && rect.top > spaceBelow;
    const width = Math.max(rect.width, 240);
    const left = Math.max(margin, Math.min(rect.left, viewportWidth - margin - width));

    const next = {
      position: "fixed",
      top: "auto",
      bottom: "auto",
      left: `${left}px`,
      right: "auto",
      minWidth: `${rect.width}px`,
      zIndex,
    };
    if (openUp) {
      next.bottom = `${viewportHeight - rect.top + gap}px`;
    } else {
      next.top = `${rect.bottom + gap}px`;
    }
    setStyle(next);
  }, [controlRef, zIndex]);

  useLayoutEffect(() => {
    if (!isOpen) {
      setStyle(null);
      return undefined;
    }
    update();
    const handler = () => update();
    window.addEventListener("scroll", handler, true);
    window.addEventListener("resize", handler);
    return () => {
      window.removeEventListener("scroll", handler, true);
      window.removeEventListener("resize", handler);
    };
  }, [isOpen, update]);

  return style;
};

export default useFloatingPanel;
